import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AppConfigService } from '../app-config/app-config.service';
import { InitialConfigService } from '../app-config/initial-config.service';
import { JwtHelperService } from '../jwt/jwt-helper.service';
import { IRecaptcha } from './models/recaptcha.model';
import { IRecaptchaResult } from './models/recaptcha-result.model';
import { IGoogleJwt } from '../jwt/google-jwt.model';
import { IOrderItem } from '@core/order/models/order-item.model';
import { CRouteList, CRoutesWithParametersList } from '@app/route-list.const';
import { VenueConfigService } from '@core/venue-config/venue-config.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class RollerRecaptchaService {
  private recaptchaJwt = '';
  private executeRecaptcha$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private recaptchaResolved$: Subject<string> = new Subject<string>();
  private isAutoEnabled = false;

  constructor(
    private http: HttpClient,
    private router: Router,
    private appConfigService: AppConfigService,
    private initialConfigService: InitialConfigService,
    private jwtHelperService: JwtHelperService,
    private venueConfigService: VenueConfigService
  ) {}

  isRecaptchaRequired(items: IOrderItem[] = []): boolean {
    if (this.isAutoEnabled) {
      return true;
    }

    const route = this.router.url;
    const config = this.venueConfigService.getVenueConfig();
    if (config.venueSettings.isRecaptchaEnabled && this.isRecaptchaPage(route)) {
      return true;
    }

    if (config.detailsPage.useRecaptcha && this.isCostOrPaymentPage(route)) {
      return true;
    }

    if (
      this.isCostOrPaymentPage(route) &&
      this.hasGiftCardItem(items) &&
      config.venueSettings.forceRecaptchaForGiftCards
    ) {
      return true;
    }

    return false;
  }

  /**
   * Resolves if recaptcha is fresh or not required, otherwise sends a request to execute new recaptcha and returns the
   * Subject that will resolve or error
   */
  getRecaptchaIfRequired(items: IOrderItem[] = []): Observable<string> {
    if (!this.isRecaptchaRequired(items) || this.isFresh(this.recaptchaJwt)) {
      return of(this.recaptchaJwt);
    }

    this.setExecuteRecaptcha(true);
    return this.recaptchaResolved$.asObservable();
  }

  /** Gets the recaptchaJwt if it is in memory. */
  getRecaptchaJwt(): string {
    return this.recaptchaJwt;
  }

  setRecaptchaEnabledAutomatically() {
    this.isAutoEnabled = true;
    this.setRecaptchaJwt('');
    this.setExecuteRecaptcha(true);
  }

  /** Sets the recaptcha Jwt after the return of the draft booking. */
  setRecaptchaJwt(recaptchaJwt: string): void {
    this.recaptchaJwt = recaptchaJwt;
  }

  /** Gets the subject executeRecaptcha as an observable - listen for a trigger event. */
  getExecuteRecaptcha(): Observable<boolean> {
    return this.executeRecaptcha$.asObservable();
  }

  /** Triggers the subject to send a void event to incite the roller recaptcha component to execute a check. */
  setExecuteRecaptcha(executed: boolean): void {
    this.executeRecaptcha$.next(executed);
  }

  setRecaptchaResolved(jwt: string) {
    this.recaptchaResolved$.next(jwt);
    this.setRecaptchaJwt(jwt);
  }

  setRecaptchaFailed(error: unknown) {
    this.recaptchaResolved$.error(error);
  }

  /**
   * Validates the string response from
   * @param response string return from the Google Recaptcha element.
   * @returns observable of the jwt string.
   */
  validateGoogleRecaptcha(response: string): Observable<IRecaptchaResult | unknown> {
    const secret: string | undefined = this.appConfigService.getEnvValue<string>('recaptchaKey');
    const payload: IRecaptcha = {
      response: response,
      secret,
      isInvisible: true
    };

    return this.http
      .post<IRecaptchaResult>(`${this.initialConfigService.getBaseApiUrl()}/google/recaptcha`, payload)
      .pipe(
        tap((result: IRecaptchaResult) => {
          this.setRecaptchaResolved(result.isValid ? result.jwt : '');
          if (!result.isValid) {
            this.setRecaptchaFailed('Recaptcha result invalid from API');
          }
        })
      );
  }

  private isFresh(jwt: string) {
    return jwt.length && this.jwtHelperService.isTokenFresh<IGoogleJwt>(jwt, 'expiry');
  }

  private isRecaptchaPage(route: string): boolean {
    const page = [CRouteList.signup, CRouteList.login, ...this.getCostOrPaymentPages()];
    return page.some((p) => route.includes(p));
  }

  private isCostOrPaymentPage(route: string): boolean {
    const page = this.getCostOrPaymentPages();
    return page.some((p) => route.includes(p));
  }

  private getCostOrPaymentPages() {
    return [CRouteList.details, CRouteList.payment, CRoutesWithParametersList.ticketsMembership];
  }

  private hasGiftCardItem(items: IOrderItem[]): boolean {
    return !!items.length && items.some((item) => item.productType === 'giftcard');
  }
}
